/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {loadModules} from 'esri-loader';

/**
 * @useAssetMapping
 *
 * Provides a hook for retrieving assets mapping
 * from CVS Cluster API to SuperCluster Pattern.
 *
 * ```typescript
 *
 *    const getAssetMapping = useAssetMapping();
 *    ...
 *    Get the Assets based on the SuperCluster Pattern
 *    const assets = getAssetMapping?.current(assets, spatialReference);
 *
 * ```
 */
export const useAssetMapping = () => {
  const getAssetsMapping = useRef(null);
  useEffect(() => {
    loadModules(['esri/geometry/Point'])
      .then(([Point]) => {
        getAssetsMapping.current = function(assets: any[], spatialReference: __esri.SpatialReference): Object[] {
          const assetMapping = [];
          assets?.forEach(asset => {
            const point = new Point(
              asset?.geometry?.x,
              asset?.geometry?.y,
              spatialReference
            );
            const geometry = {
              coordinates : [point.longitude, point.latitude],
              type        : 'Point'
            };
            if (asset?.OBJECTID) {
              assetMapping.push({...asset, ...{geometry}});
            } else {
              assetMapping.push({
                id         : asset?.clusterId,
                geometry,
                extent     : asset?.extent,
                properties : {
                  cluster                 : true,
                  cluster_id              : asset?.clusterId,
                  point_count             : asset?.clusterCount,
                  point_count_abbreviated : asset?.clusterCount
                },
                type : 'Feature'
              });
            }
          });
          return assetMapping;
        };
      });
  }, []);
  return function(assets: any[], spatialReference: __esri.SpatialReference): Object[] {
    return getAssetsMapping?.current ? getAssetsMapping.current(assets, spatialReference) : [];
  };
};
  
export default useAssetMapping;
